import { useMemo } from 'react';

import { useAppSelector } from '@/store/hooks';
import { selectSessionByUuid } from '@/store/slices/project/selectors';
import { PageModule, PageModuleType } from '@/types/module.types';
import { Speaker } from '@/types/speakers.types';
import { assertIsSpeaker } from '../assertions/assert-is-speaker';
import { selectSpeakers } from '@/store/slices/speakers/selectors';
import { quietError } from '../logging';

type Props = {
  sessionUuid?: string;
};

const useSpeakersForSession = ({ sessionUuid }: Props) => {
  const session = useAppSelector(selectSessionByUuid(sessionUuid));
  const allSpeakers = useAppSelector(selectSpeakers);

  return useMemo(() => {
    if (!allSpeakers) return [];
    if (!session) return [];

    const allSpeakersMap = Object.fromEntries(allSpeakers.map((s) => [s.speaker, s]));

    const sessionSpeakers = (session?.modules || []).reduce((accumSpeakers: Speaker[], module: PageModule) => {
      if (!module.is_on) return accumSpeakers;
      if (module.type !== PageModuleType.speakers) return accumSpeakers;
      if (!module.content_modules?.length) return accumSpeakers;

      (module.modules || []).forEach((speaker: Speaker) => {
        try {
          assertIsSpeaker(speaker);
          accumSpeakers.push({
            ...speaker,
            bl_admin: allSpeakersMap?.[speaker.speaker]?.bl_admin || null,
          });
        } catch (e) {
          // this error does not mean much, it's just an improperly formatted speaker, likely from early dev.
          // can safely log the error quietly and look only if we really care.
          quietError(e);
        }
      });

      return accumSpeakers;
    }, []);

    return sessionSpeakers;
  }, [allSpeakers, session]);
};

export default useSpeakersForSession;
